import { Alert, Button, ButtonLink } from '@/design-system'
import { tryNowPath } from '@/shared/utils/tryNowPath'
import { Show, createSignal, onCleanup, onMount } from 'solid-js'
import type { HiringManager } from '../../api/types/generated'
import { useI18n } from '../../i18n'
import { AuthLayout } from '../../shared/components/AuthLayout'
import { authStore } from '../../stores/auth.store'
import { HiringManagerPromoPanel } from '../hiring-manager-auth/components/HiringManagerPromoPanel'

export function HMPendingApprovalPage() {
  const { t, locale } = useI18n()

  const status = () => authStore.hmApprovalStatus
  const isRejected = () => status() === 'rejected'
  const movedToTry = () => {
    const u = authStore.state.user
    return !!u && !isRejected() && (u as HiringManager).signup_moved_to_try === true
  }
  const title = () => {
    if (isRejected()) {
      return t('hmApproval.rejectedTitle')
    }
    return movedToTry() ? t('hmApproval.closedTitle') : t('hmApproval.pendingTitle')
  }

  const rejectionReason = () => {
    const u = authStore.state.user
    return u && 'rejection_reason' in u ? (u as HiringManager).rejection_reason : null
  }

  const [refreshing, setRefreshing] = createSignal(false)

  const POLL_INTERVAL_MS = 10_000
  onMount(() => {
    void authStore.revalidate()
    const id = setInterval(() => {
      void authStore.revalidate()
    }, POLL_INTERVAL_MS)
    onCleanup(() => clearInterval(id))
  })

  const refresh = async () => {
    setRefreshing(true)
    try {
      await authStore.revalidate()
    } finally {
      setRefreshing(false)
    }
  }

  const signOut = () => {
    void authStore.logout()
  }

  return (
    <AuthLayout
      title={title()}
      subtitle=""
      promo={<HiringManagerPromoPanel />}
      promoTone="secondary"
    >
      <div class="space-y-4">
        <Show
          when={isRejected()}
          fallback={
            <Show
              when={movedToTry()}
              fallback={
                <p class="text-sm text-[var(--color-text-muted)] text-center">
                  {t('hmApproval.pendingBody')}
                </p>
              }
            >
              <div class="space-y-2 text-sm text-[var(--color-text-muted)] text-center">
                <p>{t('hmApproval.closedBody')}</p>
                <p>{t('hmApproval.closedInvited')}</p>
              </div>
            </Show>
          }
        >
          <Alert variant="error">
            <p class="text-sm">{t('hmApproval.rejectedBody')}</p>
          </Alert>
          <Show when={rejectionReason()}>
            <div class="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
              <p class="text-xs font-medium text-[var(--color-text-muted)] mb-1">
                {t('hmApproval.reasonLabel')}
              </p>
              <p class="text-sm text-[var(--color-text)]">{rejectionReason()}</p>
            </div>
          </Show>
        </Show>

        <div class="space-y-3 pt-2">
          <Show when={movedToTry()}>
            <ButtonLink
              variant="super-primary"
              fullWidth
              href={tryNowPath(locale(), 'pending_approval')}
            >
              {t('hmApproval.tryNowCta')}
            </ButtonLink>
          </Show>
          <Show when={!isRejected()}>
            <Button
              variant={movedToTry() ? 'ghost' : 'super-primary'}
              fullWidth
              onClick={refresh}
              loading={refreshing()}
            >
              {t('hmApproval.refresh')}
            </Button>
          </Show>
          <Button variant="ghost" fullWidth onClick={signOut}>
            {t('hmApproval.logout')}
          </Button>
        </div>
      </div>
    </AuthLayout>
  )
}
